"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
	submitSupportRequest,
	verifySupportOtp,
	resendSupportOtp,
} from "@/services/support-api-client";

declare global {
	interface Window {
		turnstile?: {
			render: (
				container: HTMLElement | string,
				options: {
					sitekey: string;
					callback: (token: string) => void;
					"expired-callback"?: () => void;
					"error-callback"?: () => void;
				}
			) => string;
			remove: (widgetId: string) => void;
			reset: (widgetId: string) => void;
		};
	}
}

export const TURNSTILE_SITE_KEY =
	process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim() || "";

export type SupportContactStep = "form" | "verify" | "done";

export function useSupportContactFlow(options?: {
	requireTermsAgreement?: boolean;
}) {
	const requireTerms = options?.requireTermsAgreement !== false;

	const [formData, setFormData] = useState({
		name: "",
		email: "",
		phone: "",
		message: "",
	});
	const [agreedToTerms, setAgreedToTerms] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [step, setStep] = useState<SupportContactStep>("form");
	const [requestId, setRequestId] = useState<string | null>(null);
	const [caseNumber, setCaseNumber] = useState<string | null>(null);
	const [verifyEmail, setVerifyEmail] = useState("");
	const [otp, setOtp] = useState("");
	const [isVerifying, setIsVerifying] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [resendCooldown, setResendCooldown] = useState(0);
	const [emailVerified, setEmailVerified] = useState(false);
	const [copied, setCopied] = useState(false);

	const idempotencyKeyRef = useRef<string | null>(null);
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const turnstileContainerRef = useRef<HTMLDivElement>(null);
	const turnstileWidgetIdRef = useRef<string | null>(null);

	const onTurnstileToken = useCallback((token: string | null) => {
		setTurnstileToken(token);
	}, []);

	useEffect(() => {
		if (step !== "form" || !TURNSTILE_SITE_KEY) return;

		let cancelled = false;

		const tryMount = () => {
			if (cancelled || turnstileWidgetIdRef.current) return false;
			const el = turnstileContainerRef.current;
			if (!el || !window.turnstile) return false;
			turnstileWidgetIdRef.current = window.turnstile.render(el, {
				sitekey: TURNSTILE_SITE_KEY,
				callback: (t) => onTurnstileToken(t),
				"expired-callback": () => onTurnstileToken(null),
				"error-callback": () => onTurnstileToken(null),
			});
			return true;
		};

		if (tryMount()) {
			return () => {
				cancelled = true;
				if (turnstileWidgetIdRef.current && window.turnstile) {
					try {
						window.turnstile.remove(turnstileWidgetIdRef.current);
					} catch {
						/* ignore */
					}
					turnstileWidgetIdRef.current = null;
				}
			};
		}

		const poll = window.setInterval(() => {
			if (tryMount()) {
				window.clearInterval(poll);
			}
		}, 100);

		return () => {
			cancelled = true;
			window.clearInterval(poll);
			if (turnstileWidgetIdRef.current && window.turnstile) {
				try {
					window.turnstile.remove(turnstileWidgetIdRef.current);
				} catch {
					/* ignore */
				}
				turnstileWidgetIdRef.current = null;
			}
		};
	}, [onTurnstileToken, step]);

	const formatPhoneNumber = (value: string) => {
		const phoneNumber = value.replace(/\D/g, "");
		if (phoneNumber.length <= 3) {
			return phoneNumber;
		}
		if (phoneNumber.length <= 6) {
			return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
		}
		return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
	};

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({
			...prev,
			phone: formatPhoneNumber(e.target.value),
		}));
	};

	const resetFlow = useCallback(() => {
		setStep("form");
		setRequestId(null);
		setVerifyEmail("");
		setOtp("");
		setEmailVerified(false);
		idempotencyKeyRef.current = null;
		setTurnstileToken(null);
		if (turnstileWidgetIdRef.current && window.turnstile) {
			try {
				window.turnstile.reset(turnstileWidgetIdRef.current);
			} catch {
				/* ignore */
			}
		}
	}, []);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.name || !formData.email || !formData.message) {
			toast.error("Please fill in all required fields");
			return;
		}

		if (requireTerms && !agreedToTerms) {
			toast.error(
				"Please agree to the terms & conditions and Privacy Policy"
			);
			return;
		}

		if (TURNSTILE_SITE_KEY && !turnstileToken) {
			toast.error("Complete the verification below before sending");
			return;
		}

		if (!idempotencyKeyRef.current) {
			idempotencyKeyRef.current =
				typeof crypto !== "undefined" && crypto.randomUUID
					? crypto.randomUUID()
					: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		}

		setIsSubmitting(true);

		try {
			const data = await submitSupportRequest(idempotencyKeyRef.current, {
				name: formData.name.trim(),
				email: formData.email.trim(),
				message: formData.message.trim(),
				phone: formData.phone || null,
			});

			setRequestId(data.request_id);
			setCaseNumber(data.case_number || null);
			setVerifyEmail(formData.email.trim());

			if (data.is_verified) {
				setEmailVerified(true);
				setStep("done");
				toast.success(data.message || "Request received.");
			} else {
				setStep("verify");
				setOtp("");
				toast.success(
					data.message ||
						"Request submitted. Verify your email for priority support."
				);
			}

			if (data.duplicate) {
				toast.message("We already have this request on file.");
			}
		} catch (error) {
			console.error("Support submit error:", error);
			setTurnstileToken(null);
			if (turnstileWidgetIdRef.current && window.turnstile) {
				try {
					window.turnstile.reset(turnstileWidgetIdRef.current);
				} catch {
					/* ignore */
				}
			}
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to submit. Please try again."
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleVerifyOtp = async () => {
		if (!requestId || otp.length !== 6) {
			toast.error("Enter the 6-digit code from your email");
			return;
		}
		setIsVerifying(true);
		try {
			const res = await verifySupportOtp({ request_id: requestId, otp });
			setEmailVerified(true);
			setStep("done");
			toast.success(res.message || "Email verified successfully");
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Invalid or expired code"
			);
		} finally {
			setIsVerifying(false);
		}
	};

	useEffect(() => {
		if (resendCooldown <= 0) return;
		const t = window.setTimeout(
			() => setResendCooldown((c) => c - 1),
			1000
		);
		return () => window.clearTimeout(t);
	}, [resendCooldown]);

	const handleResendOtp = async () => {
		if (!requestId || resendCooldown > 0) return;
		setIsResending(true);
		try {
			const res = await resendSupportOtp({ request_id: requestId });
			toast.success(res.message || "OTP resent successfully");
			setResendCooldown(60);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Could not resend code"
			);
		} finally {
			setIsResending(false);
		}
	};

	const handleCopyCaseNumber = async () => {
		if (caseNumber) {
			await navigator.clipboard.writeText(caseNumber);
			setCopied(true);
			toast.success("Case Number copied");
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const skipVerifyToDone = useCallback(() => {
		setEmailVerified(false);
		setStep("done");
	}, []);

	const clearFormAfterSuccess = useCallback(() => {
		setFormData({
			name: "",
			email: "",
			phone: "",
			message: "",
		});
		setAgreedToTerms(false);
	}, []);

	return {
		formData,
		setFormData,
		agreedToTerms,
		setAgreedToTerms,
		isSubmitting,
		step,
		requestId,
		caseNumber,
		verifyEmail,
		otp,
		setOtp,
		isVerifying,
		isResending,
		resendCooldown,
		emailVerified,
		copied,
		turnstileToken,
		turnstileContainerRef,
		TURNSTILE_SITE_KEY,
		formatPhoneNumber,
		handlePhoneChange,
		resetFlow,
		handleSubmit,
		handleVerifyOtp,
		handleResendOtp,
		handleCopyCaseNumber,
		clearFormAfterSuccess,
		skipVerifyToDone,
	};
}
